import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CoverImage, ArticleMeta, CategoryTag } from "./ArticleCard";
import { articlePath } from "../lib/format";

const typeLabel = (a) => (a.type === "review" ? "Review" : "Guide");

function LeadStory({ article }) {
  return (
    <Link to={articlePath(article)} className="group block min-w-0" data-testid={`featured-lead-${article.slug}`}>
      <div className="relative aspect-[16/10] rounded-xl overflow-hidden bg-neutral-100 mb-7">
        <CoverImage src={article.image_url} alt={article.title} />
        <CategoryTag>{article.category}</CategoryTag>
      </div>
      <p className="eyebrow mb-4">{typeLabel(article)} · Editor's pick</p>
      <h3 className="font-playfair text-3xl sm:text-4xl lg:text-[44px] text-neutral-900 tracking-tight leading-[1.08] mb-4 group-hover:text-indigo-900 transition-colors">
        {article.title}
      </h3>
      <p className="text-base sm:text-lg text-neutral-600 leading-relaxed mb-6 max-w-2xl">{article.excerpt}</p>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <ArticleMeta article={article} />
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-800">
          Read article <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

function SupportingStory({ article }) {
  return (
    <Link
      to={articlePath(article)}
      className="group grid grid-cols-1 sm:grid-cols-[minmax(0,200px)_1fr] lg:grid-cols-1 xl:grid-cols-[minmax(0,180px)_1fr] gap-5 min-w-0"
      data-testid={`featured-support-${article.slug}`}
    >
      <div className="relative aspect-[16/10] sm:aspect-[4/3] lg:aspect-[16/9] xl:aspect-[4/3] rounded-lg overflow-hidden bg-neutral-100">
        <CoverImage src={article.image_url} alt={article.title} />
      </div>
      <div className="min-w-0">
        <p className="eyebrow mb-2">{article.category}</p>
        <h3 className="font-playfair text-xl text-neutral-900 leading-snug mb-2 group-hover:text-indigo-900 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-neutral-600 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
        <ArticleMeta article={article} />
        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-indigo-800">
          Read article <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

export default function FeaturedEditorial({ articles = [] }) {
  if (!articles.length) return null;
  const [lead, ...rest] = articles;
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="featured-editorial">
      <div className="flex items-end justify-between gap-6 mb-12 border-b border-neutral-200 pb-6">
        <div>
          <p className="eyebrow mb-3">Featured</p>
          <h2 className="font-playfair text-3xl sm:text-4xl text-neutral-900 tracking-tight leading-tight">
            Editor's picks
          </h2>
        </div>
        <Link to="/reviews" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-indigo-800 hover:text-indigo-950" data-testid="featured-editorial-all">
          All reviews <ArrowRight size={14} />
        </Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14">
        <div className="lg:col-span-7 min-w-0">
          <LeadStory article={lead} />
        </div>
        <div className="lg:col-span-5 min-w-0 flex flex-col gap-10 lg:border-l lg:border-neutral-200 lg:pl-12">
          {rest.slice(0, 2).map((a, i) => (
            <div key={a.id} className={i > 0 ? "pt-10 border-t border-neutral-200" : ""}>
              <SupportingStory article={a} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
